import React, { useState, useEffect } from "react";
import Header from "../ui/Header";
import Footer from "../ui/Footer";

const ProfilePage = () => {
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Populate email from stored user info
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    try {
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user?.email) setEmail(user.email);
      }
    } catch (err) {
      console.warn("Could not parse stored profile data.", err);
    }
  }, []);

  useEffect(() => {
    if (!profilePic) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(profilePic);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profilePic]);

  const handlePicUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePic(file);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    // Determine if user is trying to change password
    const wantsPasswordChange =
      currentPassword.trim() || newPassword.trim() || confirmPassword.trim();

    if (wantsPasswordChange) {
      // All password fields must be filled
      if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
        setErrorMsg("To change your password, fill in current, new, and confirm password.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setErrorMsg("New password and confirmation do not match.");
        return;
      }

      if (newPassword.length < 8) {
        setErrorMsg("New password must be at least 8 characters long.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Build payload for backend
      const payload = {
        email,
        // only send password fields if they actually want to change password
        ...(wantsPasswordChange && {
          currentPassword,
          newPassword,
        }),
        // profilePic could be sent via FormData in a real API call
      };

      console.log("UPDATE USER DATA (stub):", payload, profilePic);

      // TODO: integrate with your real backend / auth service, e.g.:
      // await authService.updateProfile(payload, profilePic);

      // Simulate success for now
      setSuccessMsg("Profile updated successfully.");
      if (wantsPasswordChange) {
        // Clear password fields after successful change
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }

      // Optionally update localStorage email so it stays in sync
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...user,
              email,
            })
          );
        } catch (err) {
          console.warn("Could not update stored profile data.", err);
        }
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setErrorMsg("Something went wrong while updating your profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC]">
      <Header />

      {/* MAIN CONTENT */}
      <main className="flex-1 max-w-3xl mx-auto py-10 px-6 font-[Manrope]">
        <h1 className="text-3xl font-bold text-[#0EA5E9] mb-2">
          Profile Settings
        </h1>
        <p className="text-sm text-[#64748B] mb-6">
          Manage your account details and update your password securely.
        </p>

        <form onSubmit={handleUpdate} className="space-y-8 bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 sm:p-8">
          {/* Profile Picture */}
          <div className="flex items-center gap-6">
            <div className="h-20 w-20 rounded-full border border-[#CBD5E1] overflow-hidden bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="white"
                  className="w-10 h-10"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                  />
                </svg>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-[#0F172A] mb-1">
                Profile Picture
              </p>
              <label className="cursor-pointer text-[#0EA5E9] text-sm font-semibold">
                Change Photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePicUpload}
                />
              </label>
              <p className="text-xs text-[#94A3B8] mt-1">
                Recommended: square image, at least 128×128px.
              </p>
            </div>
          </div>

          {/* Account Info */}
          <div className="border-t border-[#E2E8F0] pt-6">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-3">
              Account Information
            </h2>

            {/* Email */}
            <div className="mb-4">
              <label
                htmlFor="profile-email"
                className="block text-sm font-medium text-[#334155] mb-1"
              >
                Email
              </label>
              <input
                id="profile-email"
                type="email"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0EA5E9]/60 focus:outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
              <p className="text-xs text-[#94A3B8] mt-1">
                This email is used to sign in and receive notifications.
              </p>
            </div>
          </div>

          {/* Password Section */}
          <div className="border-t border-[#E2E8F0] pt-6">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-3">
              Change Password
            </h2>
            <p className="text-xs text-[#94A3B8] mb-4">
              To change your password, enter your current password and a new one.
            </p>

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label
                  htmlFor="profile-current-password"
                  className="block text-sm font-medium text-[#334155] mb-1"
                >
                  Current Password
                </label>
                <input
                  id="profile-current-password"
                  type="password"
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0EA5E9]/60 focus:outline-none"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password…"
                />
              </div>

              {/* New Password */}
              <div>
                <label
                  htmlFor="profile-new-password"
                  className="block text-sm font-medium text-[#334155] mb-1"
                >
                  New Password
                </label>
                <input
                  id="profile-new-password"
                  type="password"
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0EA5E9]/60 focus:outline-none"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password…"
                />
              </div>

              {/* Confirm New Password */}
              <div>
                <label
                  htmlFor="profile-confirm-password"
                  className="block text-sm font-medium text-[#334155] mb-1"
                >
                  Confirm New Password
                </label>
                <input
                  id="profile-confirm-password"
                  type="password"
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0EA5E9]/60 focus:outline-none"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password…"
                />
              </div>
            </div>
          </div>

          {/* Messages */}
          {errorMsg && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {errorMsg}
            </p>
          )}
          {successMsg && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              {successMsg}
            </p>
          )}

          {/* Save */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold px-6 py-3 rounded-lg transition w-full sm:w-auto ${
              isSubmitting ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;